import lodash from 'lodash'
import { unref } from 'vue-demi'
import HTTP from '@/utils/http'
import notification from '@/utils/notification'
import { save } from '@/utils/xfs'
import { markTenantProjectsDashboardListStale } from '@/store/dashboardListStale'

export async function createTenantProject (currentTenantProject, isAccept) {
  try {
    let requestParams = {
      ...currentTenantProject,
      isAccept,
    }
    if (currentTenantProject.type) {
      requestParams = {
        ...requestParams,
        type: currentTenantProject.type,
      }
    }
    const response = await HTTP.post('/tenant_projects', { ...requestParams })
    markTenantProjectsDashboardListStale()
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err, 'tenant project')
    return null
  }
}

export async function loadTenantProject (id, isSilentGet = false) {
  try {
    const get = isSilentGet ? HTTP.silentGet : HTTP.get
    const response = await get(`/tenant_projects/${id}`)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err, 'tenant project')
    return null
  }
}

export async function isUpdateable (id) {
  try {
    const response = await HTTP.get(`/tenant_projects/${id}/status`)
    const status = response.data.data
    const isUpdateable = status !== 'WITHDRAWN' && status !== 'CANCELLED'
    if (!isUpdateable) {
      notification.fireNotification(false, `Tenant project has been ${status}`)
    }
    return isUpdateable
  } catch (err) {
    notification.handleResponseError(err, 'tenant project')
    return null
  }
}

export async function updateTenantProjectAllInfo (project, needNotify = true) {
  try {
    const noUpdatePath = ['submissions', 'deposit', 'projectDocs', 'refund']
    const updateInfo = lodash.omit(project, noUpdatePath)
    const response = await HTTP.put(`/tenant_projects/${project.id}`, updateInfo)
    if (needNotify) {
      notification.handleResponseAlert(response)
    }
    markTenantProjectsDashboardListStale()
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err, 'tenant project')
    return null
  }
}

export async function updateAccounts (project, needNotify = true) {
  try {
    const response = await HTTP.put(`/tenant_projects/${project.id}`, { accounts: project.accounts })
    if (needNotify) {
      notification.handleResponseAlert(response)
    }
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err, 'tenant project')
    throw err
  }
}

export async function updateTenantProjectStatus (id, request, action) {
  try {
    const response = await HTTP.patch(`/tenant_projects/${id}`, request, { headers: { 'X-CMS-Action': action } })
    notification.handleResponseAlert(response)
    markTenantProjectsDashboardListStale()
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err, 'tenant project')
    return null
  }
}

export async function sendFinalReminder (fid, request) {
  try {
    const response = await HTTP.post(`/tenant_projects/final_document/${unref(fid)}/reminder`, request)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function getFinalDocsHistories (fid) {
  try {
    const response = await HTTP.silentGet(`/tenant_projects/final_document/${unref(fid)}/histories`)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function getFinalDocsFolders (fid) {
  try {
    const response = await HTTP.silentGet(`/tenant_projects/final_document/${unref(fid)}/folders`)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function appendFinalDocsFolders (fid, body) {
  try {
    const response = await HTTP.post(`/tenant_projects/final_document/${unref(fid)}/folders`, body)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function updateFinalDocsFolder (finalDocId, folderId, body) {
  try {
    const response = await HTTP.put(`/tenant_projects/final_document/${unref(finalDocId)}/folders/${unref(folderId)}`, body)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function deleteFinalDocsFolder (finalDocId, folderId) {
  try {
    const response = await HTTP.delete(`/tenant_projects/final_document/${unref(finalDocId)}/folders/${unref(folderId)}`)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function list (params, isSilentGet = false) {
  try {
    let response
    if (isSilentGet) {
      response = await HTTP.silentGet('/tenant_projects/', { params })
    } else {
      response = await HTTP.get('/tenant_projects/', { params })
    }
    return response.data.data || []
  } catch (err) {
    notification.handleResponseError(err, 'tenant project')
    return []
  }
}

export async function getFitoutTabPin (params) {
  try {
    const response = await HTTP.silentGet('/tenant_projects/fitout-tab-pin', { params })
    return response.data.data || {}
  } catch (err) {
    notification.handleResponseError(err, 'tenant project fitout tab pin')
    return {}
  }
}

export async function listAll (params, diskCacheKey) {
  try {
    const response = await HTTP
      .diskCache(diskCacheKey)
      .get('/tenant_projects/all', { params })
    return response.data.data || []
  } catch (err) {
    notification.handleResponseError(err, 'tenant project')
    return []
  }
}
export async function exportAllTenantProjects (query) {
  const response = await HTTP.get('/tenant_projects/all/export', { params: query, responseType: 'arraybuffer' })
  save(response)
}

export async function invite (tenantProjectId, request) {
  try {
    const response = await HTTP.post(`/tenant_projects/${tenantProjectId}/invite/`, request)
    notification.handleResponseAlert(response)
    return response.data.data || []
  } catch (err) {
    notification.handleResponseError(err, 'tenant project invite user failed')
    return null
  }
}

export async function del (tenantProjectId) {
  try {
    const response = await HTTP.delete(`/tenant_projects/${tenantProjectId}`)
    return response.data.data || []
  } catch (err) {
    notification.handleResponseError(err, 'delete draft tenant project failed')
    return null
  }
}

export async function updateDepositRefund (depositRefundId, request) {
  try {
    const response = await HTTP.put(`/tenant_projects/${depositRefundId}/deposit_refund`, request)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err, 'update Deposit Amount error')
    return null
  }
}

export async function notifyAccountingDepositRefund (depositRefundId, request) {
  try {
    const response = await HTTP.put(`/tenant_projects/${depositRefundId}/deposit_refund`, request)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err, 'notify deposit refund accounting error')
    return null
  }
}

export async function updateAndNotify (id, request, action) {
  try {
    const response = await HTTP.put(`/tenant_projects/${id}/update/notification`, request, { headers: { 'X-CMS-Action': action } })
    notification.handleResponseAlert(response)
    markTenantProjectsDashboardListStale()
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    return null
  }
}

export async function updateFinalDocument (finalId, request) {
  try {
    const response = await HTTP.patch(`/tenant_projects/final_document/${unref(finalId)}`, request)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function getFinalDocument (id) {
  try {
    const response = await HTTP.get(`/tenant_projects/${unref(id)}/final_document`)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

// Tenant Submission API
export async function getTenantSubmission (submissionId) {
  try {
    const response = await HTTP.get(`/tenant_submissions/${submissionId}`)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err, 'tenant project')
    return null
  }
}

export async function createTenantSubmission (projectId, submissionAttr) {
  try {
    const response = await HTTP.post('/tenant_submissions', { projectId, ...submissionAttr })
    markTenantProjectsDashboardListStale()
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    return null
  }
}

export async function updateTenantSubmission (tenantSubmission, needNotificate = true, auditLogAction = {}) {
  try {
    const headers = {}
    if (auditLogAction) {
      headers['X-CMS-Action'] = auditLogAction
    }
    const response = await HTTP.put(`/tenant_submissions/${tenantSubmission.id}`, tenantSubmission, { headers })
    if (needNotificate) {
      notification.handleResponseAlert(response)
    }
    markTenantProjectsDashboardListStale()
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function updateSubmissionBatchStatus (tenantSubmission, auditLogAction = {}) {
  try {
    const headers = {}
    if (auditLogAction) {
      headers['X-CMS-Action'] = auditLogAction
    }
    const response = await HTTP.put(`/tenant_submissions/batchStatus/${tenantSubmission.id}`, tenantSubmission, { headers })
    markTenantProjectsDashboardListStale()
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    return []
  }
}

export async function updateTenantSubmissionByPath (id, request) {
  try {
    const response = await HTTP.put(`/tenant_submissions/pathValue/${id}`, request)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    return []
  }
}

export async function cancelTenantSubmissionBatch (id, request) {
  try {
    const response = await HTTP.delete(`/tenant_submissions/batch/${id}`, { data: request })
    markTenantProjectsDashboardListStale()
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function updateReinstatementAndSendSubmissionEmail (id, request) {
  try {
    const response = await HTTP.patch(`/tenant_projects/${id}/reinstatement_submitted/submission_email`, request)
    markTenantProjectsDashboardListStale()
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}
